import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, ChevronRight } from "lucide-react";
import { api, formatError, resolveAsset } from "../lib/api";

function initials(n) {
  return (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

export default function GroupMatchEditor({ match, teamsById, onSaved }) {
  const pids = match.participant_ids || [];
  const init = (match.participant_scores && match.participant_scores.length === 4)
    ? match.participant_scores
    : [0, 0, 0, 0];
  const [scores, setScores] = useState(init);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const fresh = (match.participant_scores && match.participant_scores.length === 4)
      ? match.participant_scores
      : [0, 0, 0, 0];
    setScores(fresh);
  }, [match]);

  // Ranking preview — live as you type
  const previewRanks = (() => {
    const entries = pids.map((id, idx) => ({
      idx,
      id,
      score: Number(scores[idx]) || 0,
    })).filter((e) => e.id);
    // sort desc by score, ties broken by slot order
    entries.sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
    const rankBySlot = {};
    entries.forEach((e, i) => { rankBySlot[e.idx] = i + 1; });
    return rankBySlot;
  })();

  const allFilled = pids.every(Boolean);
  const hasScore = scores.some((s) => Number(s) > 0);

  const save = async () => {
    if (!allFilled) {
      toast.error("All 4 player slots must be filled before recording scores.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        participant_scores: scores.map((s) => Number(s) || 0),
      };
      const { data } = await api.put(`/matches/${match.id}`, payload);
      toast.success(data.status === "completed" ? "Top 2 advanced to the next round" : "Scores saved");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div data-testid={`group-editor-${match.id}`} className="space-y-2">
      <div className="text-[10px] uppercase tracking-[0.25em] text-yellow-300/80 font-mono pb-1 flex items-center gap-2">
        <ChevronRight size={10} /> Top 2 advance
      </div>
      {[0, 1, 2, 3].map((slot) => {
        const pid = pids[slot];
        const team = pid ? teamsById[pid] : null;
        const rank = previewRanks[slot];
        const advancing = rank === 1 || rank === 2;
        return (
          <div
            key={slot}
            data-testid={`group-slot-${match.id}-${slot}`}
            className={`flex items-center gap-3 p-2.5 rounded-xl transition-all border
              ${advancing && hasScore ? "border-yellow-400/50 bg-yellow-400/5" : "border-white/10 bg-black/30"}`}
          >
            {team?.logo_url ? (
              <img src={resolveAsset(team.logo_url)} alt="" className="h-7 w-7 rounded-full object-cover border border-white/10" />
            ) : (
              <div
                className="h-7 w-7 rounded-full flex items-center justify-center font-display font-bold text-[10px]"
                style={{ background: team?.color || "#2a2a2a" }}
              >
                {team ? initials(team.name) : "?"}
              </div>
            )}
            <span className="flex-1 font-semibold truncate text-sm">
              {team?.name || <span className="text-zinc-600">TBD</span>}
            </span>
            {rank && hasScore && (
              <span
                className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded
                  ${advancing ? "text-yellow-200 bg-yellow-400/20" : "text-zinc-500 bg-white/5"}`}
              >
                #{rank}
              </span>
            )}
            <input
              type="number"
              value={scores[slot]}
              onChange={(e) => {
                const next = [...scores];
                next[slot] = e.target.value;
                setScores(next);
              }}
              disabled={!team}
              data-testid={`group-score-${match.id}-${slot}`}
              className="w-16 bg-black/50 border border-white/10 rounded-lg px-2 py-1.5 text-center font-mono font-bold text-base focus:outline-none focus:border-red-500"
            />
          </div>
        );
      })}
      <button
        onClick={save}
        disabled={busy || !allFilled}
        data-testid={`group-save-${match.id}`}
        className="btn-liquid !py-2 !px-4 !text-xs w-full mt-3"
      >
        <Save size={12} /> {busy ? "Saving..." : "Save scores & advance top 2"}
      </button>
      {!allFilled && (
        <div className="text-[10px] text-zinc-500 pt-1">
          Waiting for all 4 players to advance from prior matches.
        </div>
      )}
    </div>
  );
}
